import { Text } from '@vidahealth/vida-ui';
import { useEffect, useRef, type ReactNode } from 'react';
import { Animated, View } from 'react-native';

// Floating-label animation geometry. The label doubles as the placeholder: at
// rest (progress 0) it sits full-size, vertically centered in the field; floated
// (progress 1) it shrinks and rises so its vertical mid lands on the top border.
const LABEL_FLOAT_TOP = -10;
const LABEL_LINE_HEIGHT = 20; // label text-box height, used to vertically center at rest
const LABEL_REST_FALLBACK_TOP = 16; // resting top before the field height is measured
const LABEL_FLOAT_SCALE = 0.75; // 16px → 12px
const LABEL_ANIM_DURATION = 150;
const INPUT_BORDER_HEIGHT = 2; // onLayout reports the outer box; borders (1px each side) aren't content

type FloatingLabelProps = {
  /** Label text — also serves as the resting placeholder. */
  label: ReactNode;
  /** Floated (up + shrunk) when true; rests at the placeholder position when false. */
  active: boolean;
  /** Measured outer (border-box) height of the field, from its `onLayout`. */
  fieldHeight: number;
  /** Tailwind text-color class for the current field state (focus / error / etc.). */
  colorClass: string;
};

/**
 * Animated Material-style floating label shared by the input fields (TextField,
 * SelectField). The owning field measures its own height via `onLayout` and
 * passes it in; this component positions an absolutely-placed label inside the
 * field's relative container and animates it between the resting (placeholder)
 * and floated positions whenever `active` flips.
 */
export function FloatingLabel({ label, active, fieldHeight, colorClass }: FloatingLabelProps) {
  const progress = useRef(new Animated.Value(active ? 1 : 0)).current;

  // Rest at the input's measured vertical center. onLayout gives the outer
  // (border-box) height, so subtract the borders to center against the inner
  // content the field text actually occupies.
  const innerHeight = fieldHeight - INPUT_BORDER_HEIGHT;
  const restTop = innerHeight > 0 ? (innerHeight - LABEL_LINE_HEIGHT) / 2 : LABEL_REST_FALLBACK_TOP;
  const labelTop = progress.interpolate({
    inputRange: [0, 1],
    outputRange: [restTop, LABEL_FLOAT_TOP],
  });
  const labelScale = progress.interpolate({
    inputRange: [0, 1],
    outputRange: [1, LABEL_FLOAT_SCALE],
  });

  useEffect(() => {
    Animated.timing(progress, {
      toValue: active ? 1 : 0,
      duration: LABEL_ANIM_DURATION,
      useNativeDriver: false,
    }).start();
  }, [active, progress]);

  return (
    <Animated.View
      pointerEvents='none'
      style={{
        position: 'absolute',
        top: labelTop,
        left: 12,
        transformOrigin: 'left center',
        transform: [{ scale: labelScale }],
      }}
    >
      <View className='bg-background-0 px-1'>
        <Text className={colorClass} style={{ fontSize: 16, lineHeight: LABEL_LINE_HEIGHT }}>
          {label}
        </Text>
      </View>
    </Animated.View>
  );
}
